import "./style.css";
import { Map, View } from "ol";
import Overlay from "ol/Overlay.js";
import TileLayer from "ol/layer/Tile";
import XYZ from "ol/source/XYZ.js";
import TileWMS from "ol/source/TileWMS.js";
import Layer from "ol/layer/WebGLTile.js";
import Source from "ol/source/ImageTile.js";
import VectorLayer from "ol/layer/Vector.js";
import VectorSource from "ol/source/Vector.js";
import { Feature } from "ol";
import { Polygon } from "ol/geom";
import { Style, Fill, Stroke } from "ol/style.js";
import { transform } from "ol/proj.js";

const BASE_URL = "https://backend.biotoken.world";

/**
 * INTA WMS source for Argentina soil carbon data
 */
const wmsSource = new TileWMS({
  url: "https://geo-backend.inta.gob.ar/geoserver/ows",
  params: {
    LAYERS: "geonode:Mapa_COS_Nacional",
    TILED: true,
    STYLES: "geonode:Mapa_COS_Nacional",
  },
  attributions: " | Instituto Nacional de Tecnología Agropecuaria",
});

/**
 * Check if coordinates are in Argentina (approximate bounds)
 * Argentina: -73.5 to -53.0 longitude, -55.0 to -21.8 latitude
 * Eastern boundary set to -53.0 to include all Argentine territory
 * (Buenos Aires: -58.4°, Misiones: -53.6°)
 */
function isInArgentina(lng, lat) {
  return lng >= -73.5 && lng <= -53.0 && lat >= -55.0 && lat <= -21.8;
}

/**
 * Popup elements
 */
const container = document.getElementById("popup");
const content = document.getElementById("popup-content");
const closer = document.getElementById("popup-closer");
const loadingIndicator = document.getElementById("loading");

/**
 * Create overlay for popup
 */
const overlay = new Overlay({
  element: container,
  autoPan: {
    animation: {
      duration: 250,
    },
  },
});

closer.onclick = function () {
  overlay.setPosition(undefined);
  closer.blur();
  return false;
};

/**
 * Global variables for approved requests
 */
let approvedRequests = [];

/**
 * Convert carbon storage (tC/ha) to COS percentage
 */
function convertCarbonStorageToCOSPercentage(carbonTonsPerHa) {
  const SOIL_MASS_PER_HA = 3900;
  const cosPercentage = (carbonTonsPerHa / SOIL_MASS_PER_HA) * 100;
  console.log(`  [Conversión] ${carbonTonsPerHa} tC/ha ÷ 3900 t suelo/ha × 100 = ${cosPercentage.toFixed(3)}% COS`);
  return {
    carbonTonsPerHa,
    cosPercentage,
    formula: `(${carbonTonsPerHa} ÷ ${SOIL_MASS_PER_HA}) × 100 = ${cosPercentage.toFixed(3)}%`,
  };
}

/**
 * Convert tokens (t CO2) to COS percentage
 */
function convertTonsToCOSPercentage(tonsCO2, hectares) {
  const CO2_TO_C_FACTOR = 0.2727;
  const SOIL_MASS_PER_HA = 3900;
  const tonsC = tonsCO2 * CO2_TO_C_FACTOR;
  const totalSoilMass = SOIL_MASS_PER_HA * hectares;
  const cosPercentage = (tonsC / totalSoilMass) * 100;
  console.log(`  [Tokens] ${tonsCO2} t CO2 × 0.2727 = ${tonsC.toFixed(3)} tC`);
  console.log(`  [Tokens] masa suelo: 3900 × ${hectares} ha = ${totalSoilMass.toLocaleString()} t`);
  console.log(`  [Tokens] ${tonsC.toFixed(3)} tC ÷ ${totalSoilMass.toLocaleString()} t × 100 = ${cosPercentage.toFixed(3)}% COS`);
  return {
    tonsCO2,
    tonsC,
    hectares,
    totalSoilMass,
    cosPercentage,
    formula: `(${tonsCO2} × 0.2727) ÷ (3900 × ${hectares}) × 100 = ${cosPercentage.toFixed(3)}%`,
    steps: [
      `1. Convertir CO2 a C: ${tonsCO2} × 0.2727 = ${tonsC.toFixed(3)} t C`,
      `2. Masa total de suelo: 3900 × ${hectares} = ${totalSoilMass.toLocaleString()} t`,
      `3. Porcentaje: (${tonsC.toFixed(3)} ÷ ${totalSoilMass.toLocaleString()}) × 100 = ${cosPercentage.toFixed(3)}%`,
    ],
  };
}

/**
 * Point-in-polygon detection using ray casting algorithm
 */
function pointInPolygon(point, polygon) {
  const x = point[0];
  const y = point[1];
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1];
    const xj = polygon[j][0], yj = polygon[j][1];
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Sample soil carbon values within a polygon
 * Uses INTA WMS for Argentina, backend API for rest of Latin America
 */
async function getBaselineValuesForPolygon(polygonCoords, areaInHectares, viewResolution) {
  let numSamples;
  if (areaInHectares >= 10000) numSamples = 50;
  else if (areaInHectares >= 5000) numSamples = 30;
  else numSamples = 20;

  const extent = new Polygon([polygonCoords]).getExtent();
  const [minX, minY, maxX, maxY] = extent;

  const samples = [];
  for (let i = 0; i < numSamples; i++) {
    let x, y, attempts = 0;
    do {
      x = minX + Math.random() * (maxX - minX);
      y = minY + Math.random() * (maxY - minY);
      attempts++;
    } while (!pointInPolygon([x, y], polygonCoords) && attempts < 50);
    if (attempts < 50) samples.push([x, y]);
  }

  // Check if polygon is in Argentina
  const centerLonLat = transform([minX + (maxX - minX) / 2, minY + (maxY - minY) / 2], "EPSG:3857", "EPSG:4326");
  const useINTA = isInArgentina(centerLonLat[0], centerLonLat[1]);
  console.log(`\n📍 Línea de base — ${samples.length} puntos de muestra · ${areaInHectares} ha · fuente: ${useINTA ? 'INTA' : 'FAO/Backend'}`);

  const baselineData = [];
  for (let i = 0; i < samples.length; i++) {
    const sample = samples[i];
    const lonLat = transform(sample, "EPSG:3857", "EPSG:4326");

    try {
      let rawValue = null;

      if (useINTA) {
        // Use INTA WMS for Argentina
        const url = wmsSource.getFeatureInfoUrl(
          sample,
          viewResolution,
          "EPSG:3857",
          { INFO_FORMAT: "application/json" }
        );
        if (url) {
          const response = await fetch(url);
          const json = await response.json();
          if (json?.features?.[0]?.properties?.GRAY_INDEX) {
            rawValue = parseFloat(json.features[0].properties.GRAY_INDEX);
            console.log(`  Punto ${i + 1} [INTA] lng:${lonLat[0].toFixed(3)} lat:${lonLat[1].toFixed(3)} → COS_tCha_inta = ${rawValue} tC/ha`);
          }
        }
      } else {
        // Use backend API for rest of Latin America
        const response = await fetch(
          `${BASE_URL}/api/v1/public/tiles/soil-carbon/query?lng=${lonLat[0]}&lat=${lonLat[1]}`
        );
        const json = await response.json();
        if (json.success && json.data.inBounds && json.data.value !== null) {
          rawValue = json.data.value;
          console.log(`  Punto ${i + 1} [FAO/Backend] lng:${lonLat[0].toFixed(3)} lat:${lonLat[1].toFixed(3)} → value = ${rawValue} tC/ha`);
        }
      }

      if (rawValue !== null && rawValue > 0) {
        const conversion = convertCarbonStorageToCOSPercentage(rawValue);
        baselineData.push({
          pointNumber: i + 1,
          rawCarbonStorage: rawValue,
          cosPercentage: conversion.cosPercentage,
          formula: conversion.formula,
        });
      } else {
        console.log(`  Punto ${i + 1} → sin dato válido`);
      }
    } catch (error) {
      console.warn(`  Error en muestra ${i + 1}:`, error);
    }
  }

  const cosValues = baselineData.map(d => d.cosPercentage);
  const sum = cosValues.reduce((acc, val) => acc + val, 0);
  const average = cosValues.length > 0 ? sum / cosValues.length : 0;
  const min = cosValues.length > 0 ? Math.min(...cosValues) : 0;
  const max = cosValues.length > 0 ? Math.max(...cosValues) : 0;

  console.log(`📊 Línea de base — ${baselineData.length}/${samples.length} puntos con dato · rango: ${min.toFixed(3)}%–${max.toFixed(3)}% · promedio: ${average.toFixed(3)}% COS`);

  return {
    points: baselineData,
    statistics: { count: baselineData.length, sum, average, min, max },
    calculationDetails: {
      numSamples,
      areaInHectares,
      formula: `Promedio = ${sum.toFixed(3)} ÷ ${baselineData.length} = ${average.toFixed(3)}%`,
    },
  };
}

/**
 * Check if click is inside any approved request polygon and compute carbon data
 */
async function calculateAccumulatedCarbon(clickPoint, viewResolution) {
  let accumulatedTokens = 0;
  let overlappingRequests = [];
  let totalBaselineData = null;
  let totalArea = 0;

  for (const request of approvedRequests) {
    if (request.markers && request.markers.length >= 3) {
      const polygonCoords = request.markers.map(marker =>
        transform([marker.longitude, marker.latitude], "EPSG:4326", "EPSG:3857")
      );

      if (pointInPolygon(clickPoint, polygonCoords)) {
        const tokens = request.tokensGiven || request.tokensIssued || 0;
        const area = request.area || 0;

        console.log(`\n🎯 Proyecto: "${request.propertyName}" · ${area} ha · ${tokens} t CO2 tokenizadas`);

        accumulatedTokens += tokens;
        totalArea += area;

        const baselineData = await getBaselineValuesForPolygon(polygonCoords, area, viewResolution);
        totalBaselineData = baselineData;

        console.log(`\n💰 Aporte de tokens — "${request.propertyName}"`);
        const tokensCOSData = convertTonsToCOSPercentage(tokens, area);

        overlappingRequests.push({
          propertyName: request.propertyName,
          crop: request.crop,
          tokens,
          area,
          compensation: request.compensation,
          activity: request.activity,
          baselineData,
          tokensCOSData,
        });
      }
    }
  }

  const totalTokensCOSData = totalArea > 0
    ? convertTonsToCOSPercentage(accumulatedTokens, totalArea)
    : null;

  return { accumulatedTokens, overlappingRequests, totalBaselineData, totalArea, totalTokensCOSData };
}

/**
 * Fetch approved requests and draw polygons on the map
 */
async function fetchApprovedRequests() {
  try {
    const response = await fetch(`${BASE_URL}/api/v1/public/requests`);
    const data = await response.json();
    approvedRequests = data.filter(request => request.status === "approved");
    console.log(`${approvedRequests.length} proyectos aprobados cargados`);
    createPolygonLayer();
  } catch (error) {
    console.error("Error cargando proyectos:", error);
  }
}

function createPolygonLayer() {
  const vectorSource = new VectorSource();

  approvedRequests.forEach((request, index) => {
    if (request.markers && request.markers.length >= 3) {
      const coordinates = request.markers.map(marker =>
        transform([marker.longitude, marker.latitude], "EPSG:4326", "EPSG:3857")
      );
      coordinates.push(coordinates[0]);

      const polygon = new Polygon([coordinates]);
      const feature = new Feature(polygon);
      feature.setProperties({
        requestId: request._id || index,
        propertyName: request.propertyName,
        crop: request.crop,
        tokensGiven: request.tokensGiven || request.tokensIssued || 0,
        area: request.area,
      });
      vectorSource.addFeature(feature);
    }
  });

  const polygonLayer = new VectorLayer({
    source: vectorSource,
    style: new Style({
      fill: new Fill({ color: "rgba(0, 255, 0, 0.1)" }),
      stroke: new Stroke({ color: "rgba(0, 255, 0, 0.7)", width: 2 }),
    }),
  });

  map.addLayer(polygonLayer);
}

/**
 * Map layers
 */
const satelliteLayer = new Layer({
  source: new Source({
    url: "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  }),
});

const soilCarbonLayer = new TileLayer({
  source: new XYZ({
    url: `${BASE_URL}/tiles/soil-carbon/{z}/{x}/{y}.png`,
    crossOrigin: "anonymous",
    maxZoom: 7,
    minZoom: 0,
  }),
  opacity: 0.8,
});

const wmsLayer = new TileLayer({
  source: wmsSource,
});

const view = new View({
  center: transform([-64.0, -15.0], "EPSG:4326", "EPSG:3857"),
  zoom: 3,
  minZoom: 0,
  maxZoom: 12,
});

const map = new Map({
  target: "map",
  layers: [satelliteLayer, soilCarbonLayer, wmsLayer],
  overlays: [overlay],
  view,
});

/**
 * Click handler
 */
map.on("singleclick", async function (evt) {
  const coordinate = evt.coordinate;
  const lonLat = transform(coordinate, "EPSG:3857", "EPSG:4326");
  const viewResolution = view.getResolution();
  const useINTA = isInArgentina(lonLat[0], lonLat[1]);

  loadingIndicator.classList.add("active");
  overlay.setPosition(undefined);

  try {
    // Check if click is inside an approved project polygon
    const carbonData = await calculateAccumulatedCarbon(coordinate, viewResolution);

    if (carbonData.accumulatedTokens > 0 && carbonData.totalBaselineData) {
      const baselineData = carbonData.totalBaselineData;
      const tokensData = carbonData.totalTokensCOSData;

      content.innerHTML = `
        <div style="padding: 10px; max-width: 560px;">
          <h3 style="color: #2c5530; margin: 0 0 15px 0;">Carbono Orgánico en Suelo</h3>

          <div style="background: #f0f8f0; padding: 10px; border-radius: 5px; margin-bottom: 12px; border-left: 4px solid #2c5530;">
            <div style="font-size: 15px; font-weight: bold; color: #1a4722; margin-bottom: 6px;">
              Línea de Base: ${baselineData.statistics.average.toFixed(3)}% COS
            </div>
          </div>

          <div style="background: #e8f4fd; padding: 10px; border-radius: 5px; margin-bottom: 12px; border-left: 4px solid #0066cc;">
            <div style="font-size: 15px; font-weight: bold; color: #0066cc; margin-bottom: 6px;">
              Aporte de Tokens: ${tokensData.cosPercentage.toFixed(3)}% COS
            </div>
          </div>

          <div style="font-size: 12px; color: #444; background: #f9f9f9; padding: 8px; border-radius: 4px;">
            <strong>${carbonData.overlappingRequests.length} proyecto(s) en esta ubicación:</strong>
            <ul style="margin: 5px 0 0 0; padding-left: 16px;">
              ${carbonData.overlappingRequests.map(r =>
                `<li>${r.propertyName}${r.area ? ` · ${r.area} ha` : ""}${r.tokens ? ` · ${r.tokens} tokens` : ""}</li>`
              ).join("")}
            </ul>
          </div>
        </div>
      `;
      overlay.setPosition(coordinate);
      return;
    }

    // No project polygon — query individual point
    let rawValue = null;

    if (useINTA) {
      // Use INTA WMS for Argentina
      const url = wmsSource.getFeatureInfoUrl(
        coordinate,
        viewResolution,
        "EPSG:3857",
        { INFO_FORMAT: "application/json" }
      );
      if (url) {
        const response = await fetch(url);
        const json = await response.json();
        if (json?.features?.[0]?.properties?.GRAY_INDEX) {
          rawValue = parseFloat(json.features[0].properties.GRAY_INDEX);
        }
      }
    } else {
      // Use backend API for rest of Latin America
      const response = await fetch(
        `${BASE_URL}/api/v1/public/tiles/soil-carbon/query?lng=${lonLat[0]}&lat=${lonLat[1]}`
      );
      const json = await response.json();
      if (json.success && json.data.inBounds && json.data.value !== null) {
        rawValue = json.data.value;
      }
    }

    if (rawValue !== null && rawValue > 0) {
      const conversion = convertCarbonStorageToCOSPercentage(rawValue);
      const cosValue = conversion.cosPercentage;

      content.innerHTML = `
        <div style="padding: 10px; min-width: 280px;">
          <h3 style="color: #2c5530; margin: 0 0 12px 0;">Carbono Orgánico del Suelo</h3>

          <div style="background: #f0f8f0; padding: 10px; border-radius: 5px; border-left: 4px solid #2c5530;">
            <div style="font-size: 16px; font-weight: bold; color: #1a4722; margin-bottom: 8px;">
              ${cosValue.toFixed(3)}% COS (Línea de Base)
            </div>
            <div style="font-size: 12px; color: #666;">
              Sin proyectos de tokenización en esta ubicación
            </div>
          </div>
        </div>
      `;
      overlay.setPosition(coordinate);
    } else {
      content.innerHTML = `
        <div style="padding: 10px; min-width: 200px;">
          <h3 style="color: #2c5530; margin: 0 0 8px 0;">Sin datos disponibles</h3>
          <div style="font-size: 12px; color: #666;">No hay información de carbono en esta ubicación.</div>
        </div>
      `;
      overlay.setPosition(coordinate);
    }
  } catch (error) {
    console.error("Error:", error);
    content.innerHTML = `
      <div style="padding: 10px; min-width: 200px;">
        <h3 style="color: #c82333; margin: 0 0 8px 0;">Error</h3>
        <div style="font-size: 12px; color: #666;">No se pudo consultar los datos.</div>
      </div>
    `;
    overlay.setPosition(coordinate);
  } finally {
    loadingIndicator.classList.remove("active");
  }
});

map.on("pointermove", function () {
  map.getTargetElement().style.cursor = "crosshair";
});

/**
 * Legend
 */
const updateLegend = function (resolution) {
  const graphicUrl = wmsSource.getLegendUrl(resolution);
  document.getElementById("legend").src = graphicUrl;
};

map.getView().on("change:resolution", function (event) {
  updateLegend(event.target.getResolution());
});

updateLegend(map.getView().getResolution());

fetchApprovedRequests();
